import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from '@pruforce/common-adminweb-sdk';
import { AdminRegistrationFormData } from '../../admin-registration.dialog';
import { AgeValidation } from 'src/app/common/types';
import { useSelector } from 'react-redux';
import {
  selectAgeValidation,
  selectGlobalRegionOptions,
  selectRegionIdTypes,
} from 'src/app/modules/Config/_redux/configSlice';
import { RegionSelectOption } from './registration-information.hook';
import { createBirthdayValidator, createIdentityDocumentValidator } from './registration-information.validators';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export type RegistrationInformationErrors = Partial<
  Record<
    | 'selectedVenue'
    | 'selectedSession'
    | 'firstname'
    | 'lastname'
    | 'localName'
    | 'identityDocumentType'
    | 'identityDocumentNumber'
    | 'gender'
    | 'birthday'
    | 'countryCode'
    | 'phoneNumber'
    | 'email',
    string
  >
>;

const READONLY_FIELDS: Array<keyof RegistrationInformationErrors> = [
  'firstname',
  'lastname',
  'localName',
  'identityDocumentType',
  'identityDocumentNumber',
  'gender',
  'birthday',
  'countryCode',
  'phoneNumber',
  'email',
];

interface ValidationResult {
  valid: boolean;
  message?: string;
  messageParams?: Record<string, string | number>;
}

interface ValidationRule {
  field: keyof RegistrationInformationErrors;
  value: unknown;
  required?: boolean;
  pattern?: RegExp;
  patternMessage?: string;
  validator?: (value: unknown) => boolean | ValidationResult;
  validatorMessage?: string;
}

export const useRegistrationInformationValidation = (formData: AdminRegistrationFormData) => {
  const t = useTranslation();
  const [errors, setErrors] = useState<RegistrationInformationErrors>({});

  const ageValidation: AgeValidation | null = useSelector(selectAgeValidation);
  const regionIdTypes = useSelector(selectRegionIdTypes);

  const regionOptions: RegionSelectOption[] = useSelector(selectGlobalRegionOptions) ?? [];

  const { selectedVenue, selectedSession, registrationInformation } = formData;

  const selectedIdentityDocumentRule = useMemo(
    () => regionIdTypes?.find((item) => item.value === registrationInformation.identityDocumentType),
    [regionIdTypes, registrationInformation.identityDocumentType],
  );
  const phoneNumberRegex = useMemo(() => {
    const region = regionOptions.find((option) => option.value === registrationInformation.countryCode);
    return new RegExp(region?.regex?.trim() || '^\\d{5,20}$');
  }, [regionOptions, registrationInformation.countryCode]);

  const birthdayValidator = useMemo(() => createBirthdayValidator(ageValidation), [ageValidation]);

  const isReadOnlyForAgent = formData?.selectedRegistrant?.accountType === 'Agent';

  const allRules: ValidationRule[] = useMemo(
    () => [
      {
        field: 'selectedVenue',
        value: selectedVenue,
        required: true,
      },
      {
        field: 'selectedSession',
        value: selectedSession,
        required: true,
      },
      {
        field: 'firstname',
        value: registrationInformation.firstname,
        required: true,
      },
      {
        field: 'lastname',
        value: registrationInformation.lastname,
        required: true,
      },
      {
        field: 'localName',
        value: registrationInformation.localName,
        required: !Boolean(registrationInformation.noChineseName),
      },
      {
        field: 'identityDocumentType',
        value: registrationInformation.identityDocumentType,
        required: true,
      },
      {
        field: 'identityDocumentNumber',
        value: registrationInformation.identityDocumentNumber,
        required: true,
        validator: createIdentityDocumentValidator(selectedIdentityDocumentRule),
        validatorMessage: selectedIdentityDocumentRule?.errMsg,
      },
      {
        field: 'gender',
        value: registrationInformation.gender,
        required: true,
      },
      {
        field: 'birthday',
        value: registrationInformation.birthday,
        required: true,
        validator: birthdayValidator,
      },
      {
        field: 'countryCode',
        value: registrationInformation.countryCode,
        required: true,
      },
      {
        field: 'phoneNumber',
        value: registrationInformation.phoneNumber,
        required: true,
        validator: (value: unknown) => phoneNumberRegex.test(String(value).trim()),
        validatorMessage: 'Common.please_input_real_phone',
      },
      {
        field: 'email',
        value: registrationInformation.email,
        required: true,
        pattern: EMAIL_REGEX,
        patternMessage: 'Common.please_enter_a_valid_email_address',
      },
    ],
    [
      selectedVenue,
      selectedSession,
      registrationInformation,
      selectedIdentityDocumentRule,
      phoneNumberRegex,
      birthdayValidator,
    ],
  );

  const rules = useMemo(() => {
    if (!isReadOnlyForAgent) {
      return allRules;
    }

    return allRules.filter((rule) => !READONLY_FIELDS.includes(rule.field));
  }, [allRules, isReadOnlyForAgent]);

  const clearError = useCallback((field?: keyof RegistrationInformationErrors) => {
    if (!field) {
      setErrors({});
      return;
    }

    setErrors((prev) => {
      if (!prev[field]) {
        return prev;
      }

      const next = { ...prev };
      delete next[field];

      return next;
    });
  }, []);

  const validateRule = useCallback(
    (rule: ValidationRule): string | undefined => {
      const value = rule.value;

      if (rule.required) {
        const isEmpty = value === null || value === undefined || (typeof value === 'string' && value.trim() === '');

        if (isEmpty) {
          return t('Common.this_field_is_mandatory');
        }
      }

      if (rule.pattern && value && !rule.pattern.test(String(value))) {
        return rule.patternMessage ? t(rule.patternMessage) : '';
      }

      if (rule.validator && value) {
        const result = rule.validator(value);

        if (typeof result === 'boolean') {
          if (!result) {
            return rule.validatorMessage ? t(rule.validatorMessage) : '';
          }

          return undefined;
        }

        if (!result.valid) {
          return result.message ? t(result.message, result.messageParams) : '';
        }
      }

      return undefined;
    },
    [t],
  );

  const validate = useCallback(() => {
    const nextErrors: RegistrationInformationErrors = {};

    rules.forEach((rule) => {
      const error = validateRule(rule);

      if (error) {
        nextErrors[rule.field] = error;
      }
    });

    setErrors(nextErrors);

    return Object.keys(nextErrors).length === 0;
  }, [rules, validateRule]);

  const validateField = useCallback(
    (field: keyof RegistrationInformationErrors) => {
      const rule = rules.find((item) => item.field === field);

      if (!rule) {
        return true;
      }

      const error = validateRule(rule);

      setErrors((prev) => {
        const next = { ...prev };

        if (error) {
          next[field] = error;
        } else {
          delete next[field];
        }

        return next;
      });

      return !error;
    },
    [rules, validateRule],
  );

  return {
    isReadOnlyForAgent,
    errors,
    validate,
    validateField,
    clearError,
  };
};
